import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowRight,
    ChevronDown,
    ChevronUp,
    MapPin,
    Clock,
    Briefcase,
} from "lucide-react";
import { buttonVariants } from "../components/ui/button";
import { CAREERS_PAGE } from "../data/careers";

const { hero, whySection, openApplication, applyFormUrl, roles } = CAREERS_PAGE;

function RoleCard({ r, i }) {
    const [open, setOpen] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.07 }}
            className="rounded-3xl border border-gray-200 hover:border-aiblue transition overflow-hidden"
            data-testid={`career-${i}`}
        >
            {/* Header — always visible */}
            <div
                className="group flex flex-col sm:flex-row sm:items-start justify-between p-6 cursor-pointer hover:bg-aiblue/5 transition"
                onClick={() => setOpen((v) => !v)}
            >
                <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-aiorange/10 text-aiorange">
                            {r.badge}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-aiblue/10 text-aiblue">
                            {r.team}
                        </span>
                    </div>
                    <h3 className="font-display font-bold text-xl mt-1">
                        {r.title}
                    </h3>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {r.location}
                        </span>
                        <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {r.type}
                        </span>
                        <span className="flex items-center gap-1">
                            <Briefcase className="w-3.5 h-3.5" />
                            {r.experience} experience
                        </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-3 max-w-2xl">
                        {r.brief}
                    </p>
                </div>
                <div className="flex items-center gap-3 mt-4 sm:mt-0 sm:ml-6 shrink-0">
                    <a
                        href={applyFormUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={buttonVariants({
                            variant: "outline",
                            className:
                                "rounded-full group-hover:bg-aiblue group-hover:text-white group-hover:border-aiblue transition",
                        })}
                        onClick={(e) => e.stopPropagation()}
                    >
                        Apply <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                    <button className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-aiblue hover:text-aiblue transition shrink-0">
                        {open ? (
                            <ChevronUp className="w-4 h-4" />
                        ) : (
                            <ChevronDown className="w-4 h-4" />
                        )}
                    </button>
                </div>
            </div>

            {/* Expanded JD */}
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: "easeInOut" }}
                        className="overflow-hidden"
                    >
                        <div className="px-6 pb-8 border-t border-gray-100 pt-6 space-y-8">
                            <div>
                                <p className="text-xs uppercase tracking-widest font-bold text-aiorange mb-2">
                                    The Role
                                </p>
                                <p className="text-sm text-gray-700 leading-relaxed">
                                    {r.roleDescription}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-widest font-bold text-aiorange mb-3">
                                    Key Responsibilities
                                </p>
                                <ul className="space-y-3">
                                    {r.responsibilities.map((res, j) => (
                                        <li
                                            key={j}
                                            className="flex gap-3 text-sm text-gray-700"
                                        >
                                            <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-aiblue shrink-0" />
                                            <span>
                                                <span className="font-semibold text-gray-900">
                                                    {res.title}:
                                                </span>{" "}
                                                {res.detail}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-widest font-bold text-aiorange mb-3">
                                    What You Bring
                                </p>
                                <ul className="space-y-3">
                                    {r.qualifications.map((q, j) => (
                                        <li
                                            key={j}
                                            className="flex gap-3 text-sm text-gray-700"
                                        >
                                            <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-aiorange shrink-0" />
                                            <span>{q}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <a
                                href={applyFormUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={buttonVariants({
                                    className:
                                        "rounded-full bg-aiblue text-white hover:bg-aiblue/90",
                                })}
                            >
                                Apply for this role{" "}
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}

export default function CareersPage() {
    return (
        <main className="pt-28 pb-20" data-testid="careers-page">
            {/* Hero */}
            <section className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
                <p className="text-xs uppercase tracking-[0.3em] text-aiorange font-bold mb-3">
                    {hero.tag}
                </p>
                <h1
                    className="font-display font-black text-4xl sm:text-6xl tracking-normal leading-[1.05]"
                    dangerouslySetInnerHTML={{ __html: hero.headline }}
                />
                <p className="mt-5 text-lg text-gray-600 max-w-2xl">
                    {hero.description}
                </p>
            </section>

            {/* Roles */}
            <section className="max-w-5xl mx-auto px-4 sm:px-8 pb-16">
                <div className="space-y-4" data-testid="careers-list">
                    {roles.map((r, i) => (
                        <RoleCard key={i} r={r} i={i} />
                    ))}
                </div>
            </section>

            {/* Why Join */}
            <section className="max-w-5xl mx-auto px-4 sm:px-8 pb-16">
                <p className="text-xs uppercase tracking-[0.3em] text-aiorange font-bold mb-2">
                    {whySection.tag}
                </p>
                <h2 className="font-display font-black text-2xl sm:text-3xl tracking-normal mb-6">
                    {whySection.heading}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {whySection.perks.map((p, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.06 }}
                            className="p-5 rounded-2xl border border-gray-100 bg-aibeige/40"
                        >
                            <p className="font-display font-bold text-base mb-1">
                                {p.label}
                            </p>
                            <p className="text-sm text-gray-600">{p.detail}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Open application */}
            <section className="max-w-5xl mx-auto px-4 sm:px-8">
                <div className="p-6 rounded-3xl bg-aibeige/50 text-center">
                    <p className="font-display font-bold text-lg">
                        {openApplication.heading}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                        {openApplication.description}{" "}
                        <a
                            href={`mailto:${openApplication.email}`}
                            className="text-aiblue font-semibold"
                        >
                            {openApplication.email}
                        </a>
                    </p>
                </div>
            </section>
        </main>
    );
}
